/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosError } from 'axios';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import pathBuilder from '@/api/utils';
import { grantOfflineAccess } from '@/api/auth/google';
import { atLeast } from '@/utils';

import {
  ApiOrgMember,
  ApiPeerGroup,
  BaseOrganization,
  BasePeerGroup,
  BaseUser,
  MemberRole,
  GettingStartedPeerGroup,
  WebRequestStatus,
  ApiPeerGroupMembership
} from '@/models';
import {
  ApiBadge,
  ApiBadgeWithObjective,
  ApiObjective,
  ApiOutcome,
  ApiPacket,
  Badge,
  Objective,
  Outcome,
  utils as PacketUtils,
  Usage,
  Packet,
  BasePacket
} from '@/models/packet';

const getSubPathFromRole = (role: MemberRole) => {
  switch (role) {
    case MemberRole.STUDENT:
      return 'members';
    case MemberRole.TEACHER:
      return 'teachers';
    case MemberRole.OBSERVER:
      return 'observers';
    default:
      throw new Error(`Unknown role: ${role}`);
  }
};

const getIdFieldNameFromRole = (role: MemberRole) => {
  switch (role) {
    case MemberRole.STUDENT:
      return 'memberUserId';
    case MemberRole.TEACHER:
      return 'teacherUserId';
    case MemberRole.OBSERVER:
      return 'observerUserId';
    default:
      throw new Error(`Unknown role: ${role}`);
  }
};

const mapApiRoleToClientRole = (role: string) => {
  switch (role) {
    case 'student':
      return MemberRole.STUDENT;
    case 'teacher':
      return MemberRole.TEACHER;
    case 'observer':
      return MemberRole.OBSERVER;
  }
};

const parseToApiPeerGroupMembership = (
  rawMembership: any
): ApiPeerGroupMembership => {
  return {
    ...rawMembership,
    role: mapApiRoleToClientRole(rawMembership.role)
  };
};

const parseToApiPeerGroup = (rawGroup: any): ApiPeerGroup => {
  return {
    ...rawGroup,
    memberships: rawGroup.memberships.map(parseToApiPeerGroupMembership)
  };
};

const importGoogleClassroomCourse = async (
  organizationId: number,
  courseId: string
): Promise<ApiPeerGroup> => {
  const {
    data: { data: result }
  } = await axios.post(
    pathBuilder.basic(
      'organizations',
      `${organizationId}`,
      'google-classroom-courses'
    ),
    snakecaseKeys({ courseId })
  );

  return parseToApiPeerGroup(camelcaseKeys(result, { deep: true }));
};

export default {
  async getUserOrganizations() {
    const { data } = await axios.get(
      pathBuilder.adminAccessible('organizations-details')
    );

    return camelcaseKeys(data.data).map((org: any) => {
      return { ...org, createdAt: new Date(org.createdAt) };
    }) as BaseOrganization[];
  },

  // This gets the groups in the org and their members (which may include
  // users _not_ in the org as of 2020-03-10 -- hopefully we can change
  // that)
  async getOrganizationStructure(
    organizationId: number
  ): Promise<ApiPeerGroup[]> {
    const { data } = await axios.get(
      pathBuilder.adminAccessible(
        'organizations',
        `${organizationId}`,
        'peer-groups?include-members=false&include-packets=true&include-memberships=true'
      )
    );

    return camelcaseKeys(data.data, {
      deep: true
    }).map(parseToApiPeerGroup);
  },

  // Returns the members of the org
  async getOrganizationMembers(organizationId: number) {
    const { data } = await axios.get(
      pathBuilder.adminAccessible(
        'organizations',
        `${organizationId}`,
        'members'
      )
    );

    return camelcaseKeys(data.data, { deep: true }) as ApiOrgMember[];
  },

  async getOrganizationPackets(organizationId: number) {
    const {
      data: { data }
    } = await axios.get(
      pathBuilder.basic(
        'organizations',
        `${organizationId}`,
        'packets?include-connected=true'
      )
    );

    const apiPackets = camelcaseKeys(data, { deep: true }) as ApiPacket[];

    return apiPackets.map(ap =>
      PacketUtils.convertApiPacket(
        ap,
        ap => ap.organization.organizationId !== organizationId && ap.public
      )
    );
  },

  async upsertUserIntoOrganization(organizationId: number, email: string) {
    const {
      data: { data }
    } = await axios.post(
      pathBuilder.adminAccessible(
        'organizations',
        `${organizationId}`,
        'members'
      ),
      {
        email
      }
    );

    return camelcaseKeys(data, { deep: true }).map(
      (d: any) => d.member
    ) as BaseUser[];
  },

  async removeOrganizationMember(organizationId: number, userId: number) {
    axios.delete(
      pathBuilder.adminAccessible(
        'organizations',
        `${organizationId}`,
        'members',
        `${userId}`
      )
    );
  },

  async updateOrganizationMember(organizationId: number, member: ApiOrgMember) {
    const {
      data: { data: results }
    } = await axios.put(
      pathBuilder.adminAccessible(
        'organizations',
        `${organizationId}`,
        'members',
        `${member.member.userId}`
      ),
      snakecaseKeys(member, { deep: true })
    );

    return camelcaseKeys(results, { deep: true }) as ApiOrgMember[];
  },

  async createPeerGroup(
    organizationId: number,
    peerGroup: { name: string; section?: string; description?: string }
  ): Promise<ApiPeerGroup> {
    const {
      data: { data: results }
    } = await axios.post(
      pathBuilder.adminAccessible(
        'organizations',
        `${organizationId}`,
        'peer-groups'
      ),
      snakecaseKeys({ peerGroup }, { deep: true })
    );

    return parseToApiPeerGroup(camelcaseKeys(results, { deep: true }));
  },

  async updatePeerGroup(
    peerGroupId: number,
    peerGroup: {
      name?: string;
      section?: string;
      description?: string;
      isActive?: boolean;
      creatorCanPublish?: boolean;
      teachersCanPublish?: boolean;
      organizationSyncAssignments?: boolean;
    }
  ) {
    const {
      data: { data: results }
    } = await axios.put(
      pathBuilder.adminAccessible('peer-groups', `${peerGroupId}`),
      snakecaseKeys({ peerGroup }, { deep: true })
    );

    return camelcaseKeys(results, { deep: true }) as BasePeerGroup;
  },

  async updateUserGroupConnection(
    userId: number,
    peerGroupId: number,
    previousRoles: MemberRole[],
    role: MemberRole
  ) {
    await Promise.all(
      previousRoles.map(async pr => {
        this.removeUserGroupConnection(userId, peerGroupId, pr);
      })
    );

    await this.addUserGroupConnection(userId, peerGroupId, role);
  },

  async addUserGroupConnection(
    userId: number,
    peerGroupId: number,
    role: MemberRole
  ): Promise<BaseUser[]> {
    const { data } = await axios.post(
      pathBuilder.adminAccessible(
        'peer-groups',
        `${peerGroupId}`,
        getSubPathFromRole(role)
      ),
      snakecaseKeys({
        [getIdFieldNameFromRole(role)]: userId
      })
    );

    return camelcaseKeys(data.data);
  },

  async removeUserGroupConnection(
    userId: number,
    peerGroupId: number,
    role: MemberRole
  ) {
    return axios.delete(
      pathBuilder.adminAccessible(
        'peer-groups',
        `${peerGroupId}`,
        getSubPathFromRole(role),
        `${userId}`
      )
    );
  },

  async getBadges(objectiveId: number) {
    const result = await axios.get(
      pathBuilder.basic('objectives', `${objectiveId}`, 'description')
    );

    const {
      data: {
        data: { badges: rawBadges }
      }
    } = result;

    const apiBadges: ApiBadge[] = camelcaseKeys(rawBadges, { deep: true });

    return apiBadges.map(b => PacketUtils.convertApiBadge(b, objectiveId));
  },

  async updateObjective(
    packetId: number,
    objective: Objective
  ): Promise<Objective> {
    const {
      data: { data: updated }
    } = await axios.put(
      pathBuilder.basic(
        'packets',
        `${packetId}`,
        'objectives',
        `${objective.objectiveId}`
      ),
      snakecaseKeys({
        objective: PacketUtils.convertObjective(objective)
      })
    );

    const apiObjective: ApiObjective = camelcaseKeys(updated, { deep: true });

    return PacketUtils.convertApiObjective(apiObjective);
  },

  async updateOutcome(packetId: number, outcome: Outcome): Promise<Outcome> {
    const {
      data: { data: updated }
    } = await axios.put(
      pathBuilder.basic(
        'packets',
        `${packetId}`,
        'outcomes',
        `${outcome.outcomeId}`
      ),
      snakecaseKeys({
        outcome: PacketUtils.convertOutcome(outcome)
      })
    );

    const apiOutcome: ApiOutcome = camelcaseKeys(updated, { deep: true });

    return PacketUtils.convertApiOutcome(apiOutcome);
  },

  async updateBadge(badge: Badge): Promise<Badge> {
    const {
      data: { data: updated }
    } = await axios.put(
      pathBuilder.basic('badges', `${badge.badgeId}`),
      snakecaseKeys({ badge })
    );

    return PacketUtils.convertApiBadgeWithObjective(
      camelcaseKeys(updated, { deep: true })
    );
  },

  async updateBadges(badges: Badge[]): Promise<Badge[]> {
    const {
      data: { data: updated }
    } = await axios.put(pathBuilder.basic('badges'), snakecaseKeys({ badges }));

    const apiBadges: ApiBadgeWithObjective[] = camelcaseKeys(updated, {
      deep: true
    });

    return apiBadges.map(b => PacketUtils.convertApiBadgeWithObjective(b));
  },

  async updateOutcomes(
    packetId: number,
    outcomes: Partial<Outcome>[],
    defaultDescription?: string
  ) {
    const {
      data: { data: results }
    } = await axios.put(
      pathBuilder.basic('packets', `${packetId}`, 'outcomes'),
      snakecaseKeys({
        outcomes: outcomes.map(o => PacketUtils.convertPartialOutcome(o)),
        description: defaultDescription || null
      })
    );

    const apiOutcomes: ApiOutcome[] = camelcaseKeys(results.outcomes, {
      deep: true
    });

    const apiBadges: ApiBadgeWithObjective[] = camelcaseKeys(results.badges, {
      deep: true
    });

    return {
      outcomes: apiOutcomes.map(o => PacketUtils.convertApiOutcome(o)),
      badges: apiBadges.map(b => PacketUtils.convertApiBadgeWithObjective(b))
    };
  },

  async updateObjectives(packetId: number, objectives: Partial<Objective>[]) {
    const {
      data: { data: results }
    } = await axios.put(
      pathBuilder.basic('packets', `${packetId}`, 'objectives'),
      snakecaseKeys({
        objectives: objectives.map(o => PacketUtils.convertPartialObjective(o))
      })
    );

    const apiObjectives: ApiObjective[] = camelcaseKeys(results.objectives, {
      deep: true
    });

    const apiBadges: ApiBadgeWithObjective[] = camelcaseKeys(results.badges, {
      deep: true
    });

    return {
      objectives: apiObjectives.map(o => PacketUtils.convertApiObjective(o)),
      badges: apiBadges.map(b => PacketUtils.convertApiBadgeWithObjective(b))
    };
  },

  // Returns the ids of all connected packets
  async connectPeerGroupToPacket(peerGroupId: number, packetId: number) {
    const {
      data: { data: results }
    } = await axios.post(
      pathBuilder.basic('peer-groups', `${peerGroupId}`, 'packets'),
      snakecaseKeys({ packetId })
    );

    const apiPackets: { packetId: number }[] = camelcaseKeys(results, {
      deep: true
    });

    return apiPackets.map(ap => ap.packetId);
  },

  async disconnectPeerGroupFromPacket(peerGroupId: number, packetId: number) {
    return axios.delete(
      pathBuilder.basic(
        'peer-groups',
        `${peerGroupId}`,
        'packets',
        `${packetId}`
      )
    );
  },

  async createPacket(organizationId: number, packet: Packet) {
    const {
      data: { data: result }
    } = await axios.post(
      pathBuilder.basic('organizations', `${organizationId}`, 'packets'),
      snakecaseKeys(
        { packet: PacketUtils.convertNewPacket(packet) },
        { deep: true }
      )
    );

    const apiPacket: ApiPacket = camelcaseKeys(result.packet, { deep: true });
    const apiBadges: ApiBadgeWithObjective[] = camelcaseKeys(result.badges, {
      deep: true
    });

    return {
      packet: PacketUtils.convertApiPacket(apiPacket, () => false),
      badges: apiBadges.map(apiBadge =>
        PacketUtils.convertApiBadgeWithObjective(apiBadge)
      )
    };
  },

  // This is a little tricky.  We want to create the packets and
  // the badges at the same time.  But badges in the api are only stored with
  // objective/outcome ids, and not directly on the packet, and we initially only
  // have temporary ids for the objectives/outcomes built during the create workflow
  // (since they haven't yet been persisted).
  //
  // So! To solve this:
  // Before we send the api request, we use the objectives/outcomes/badges built during
  // the create workflow to map the `objective.name+outcome.value` that each badge refers to.
  // After we persist them, and they are given an id, we can then look up
  // that id in the returned results via the `objective.name+outcome.value` combo.
  //
  // To add an extra layer of complication, when the api creates a packet, it also
  // generates a set of badges (with a default description) for that packet.  So we
  // need to _replace_ the existing default badges (instead of creating new ones)
  // with the badges the user has defined during the create workflow.
  // To do that, we use the objectiveId/outcomeId pairs from the previous step to lookup
  // the relevant badgeId
  async createPacketWithBadges(
    organizationId: number,
    packet: BasePacket,
    badges: Badge[],
    packetCompleteFn: (packet: BasePacket) => void,
    badgesCompleteFn: (badges: Badge[]) => void,
    packetUpdateFn: (status: WebRequestStatus) => void = () => null,
    badgesUpdateFn: (status: WebRequestStatus) => void = () => null
  ) {
    const badgeMapping = badges.reduce(
      (acc: { [key: string]: Badge }, badge) => {
        const objective = packet.objectives.find(
          o => o.objectiveId === badge.objectiveId
        );
        const outcome = packet.outcomes.find(
          o => o.outcomeId === badge.outcomeId
        );

        if (!objective || !outcome) {
          return acc;
        }

        return {
          ...acc,
          [`${objective.name}:${outcome.value}`]: badge
        };
      },
      {}
    );

    packetUpdateFn(WebRequestStatus.IN_PROGRESS);

    try {
      const { packet: createdPacket, badges: createdBadges } = await atLeast(
        this.createPacket(organizationId, packet)
      );

      packetCompleteFn(createdPacket);
      packetUpdateFn(WebRequestStatus.SUCCESS);

      // Re-assemble badges with api (persisted) objective/outcome ids
      const badgesWithUpdatedOutcomeAndObjectiveIds: Badge[] = createdPacket.outcomes.flatMap(
        outcome => {
          return createdPacket.objectives.map(objective => {
            return {
              // Lookup the input description using the created name/value
              ...badgeMapping[`${objective.name}:${outcome.value}`],
              // Override the temporary ids with the ids of the persisted
              // objective/outcome
              objectiveId: objective.objectiveId,
              outcomeId: outcome.outcomeId
            };
          });
        }
      );

      // Use api objective/outcome ids to find api badge id
      const badgesWithUpdatedBadgeId: Badge[] = badgesWithUpdatedOutcomeAndObjectiveIds.map(
        badge => {
          const badgeId = createdBadges.find(
            b =>
              b.outcomeId === badge.outcomeId &&
              b.objectiveId === badge.objectiveId
          )?.badgeId;

          if (!badgeId) {
            throw new Error(`Missing default badge for ${{ badge }}`);
          }

          return {
            ...badge,
            badgeId
          };
        }
      );

      badgesUpdateFn(WebRequestStatus.IN_PROGRESS);

      try {
        const finishedBadges = await atLeast(
          this.updateBadges(badgesWithUpdatedBadgeId)
        );

        badgesCompleteFn(finishedBadges);
        badgesUpdateFn(WebRequestStatus.SUCCESS);
      } catch (badgesError) {
        console.error({ badgesError });

        badgesUpdateFn(WebRequestStatus.FAILURE);
      }
    } catch (packetsError) {
      console.error({ packetsError });

      packetUpdateFn(WebRequestStatus.FAILURE);
    }
  },

  async updatePacketInfo(
    organizationId: number,
    packetId: number,
    packet: { name: string; description: string }
  ) {
    const {
      data: { data: result }
    } = await axios.put(
      pathBuilder.basic('packets', `${packetId}`),
      snakecaseKeys({ packet }, { deep: true })
    );

    const apiPacket: ApiPacket = camelcaseKeys(result, { deep: true });

    return PacketUtils.convertApiPacket(
      apiPacket,
      ap => ap.organization.organizationId !== organizationId && ap.public
    );
  },

  async getObjectiveUsage(packetId: number, objectiveId: number) {
    const {
      data: { data: result }
    } = await axios.get(
      pathBuilder.basic(
        'packets',
        `${packetId}`,
        'objectives',
        `${objectiveId}`,
        'usage'
      )
    );

    return camelcaseKeys(result, { deep: true }) as Usage;
  },

  async getOutcomeUsage(packetId: number, outcomeId: number) {
    const {
      data: { data: result }
    } = await axios.get(
      pathBuilder.basic(
        'packets',
        `${packetId}`,
        'outcomes',
        `${outcomeId}`,
        'usage'
      )
    );

    return camelcaseKeys(result, { deep: true }) as Usage;
  },

  async deleteOutcome(packetId: number, outcomeId: number) {
    await axios.delete(
      pathBuilder.basic('packets', `${packetId}`, 'outcomes', `${outcomeId}`)
    );
  },

  async deleteObjective(packetId: number, objectiveId: number) {
    await axios.delete(
      pathBuilder.basic(
        'packets',
        `${packetId}`,
        'objectives',
        `${objectiveId}`
      )
    );
  },

  // Useful for determining which GoogleClassrooms have already been
  // added, and for possibly redirecting lost users entering the
  // get-started workflow.
  async getPeerGroups(): Promise<GettingStartedPeerGroup[]> {
    const {
      data: { data: groups }
    } = await axios.get(pathBuilder.basic('peer-groups?include-inactive=true'));

    return camelcaseKeys(groups, { deep: true }) as GettingStartedPeerGroup[];
  },

  async createOrganization(
    name: string,
    adminId: number,
    accountType: string
  ): Promise<BaseOrganization> {
    const {
      data: { data: result }
    } = await axios.post(
      pathBuilder.basic('organizations'),
      snakecaseKeys({
        organization: { name, accountType },
        adminId
      })
    );

    return camelcaseKeys(result, { deep: true }) as BaseOrganization;
  },

  async updateOrganization(
    updatedOrg: BaseOrganization
  ): Promise<BaseOrganization> {
    const {
      data: { data: result }
    } = await axios.put(
      pathBuilder.adminAccessible(
        'organizations',
        `${updatedOrg.organizationId}`
      ),
      snakecaseKeys({
        organization: updatedOrg
      })
    );

    return camelcaseKeys(result, { deep: true }) as BaseOrganization;
  },

  async allowAccessToGoogleApi(authorizationCode: string) {
    await axios.post(
      pathBuilder.basic('google-api-auth'),
      snakecaseKeys({ authorizationCode })
    );
  },

  async importClassroom(
    organizationId: number,
    googleCourseId: string,
    retriesAllowed = 2,
    /// Set to true if we need to authorize immediately (like in the sign-up flow).
    authorizationRequired = false
  ): Promise<ApiPeerGroup> {
    try {
      if (authorizationRequired) {
        const authorizationCode = await grantOfflineAccess();

        if (!authorizationCode) {
          throw Error(
            'We need an authorization code to sync a google classroom course'
          );
        }

        await this.allowAccessToGoogleApi(authorizationCode);
      }

      // Need to await here to catch the error??
      // I think we do because of automatic promise unwrapping.
      const result = await importGoogleClassroomCourse(
        organizationId,
        googleCourseId
      );

      return result;
    } catch (error) {
      if (retriesAllowed === 0) {
        throw error;
      }

      const axiosError = error as AxiosError;

      if (axiosError.isAxiosError && axiosError.response?.status === 403) {
        const body = camelcaseKeys(axiosError.response.data);

        if (body.authorizationRequired === 'google') {
          return this.importClassroom(
            organizationId,
            googleCourseId,
            retriesAllowed - 1,
            true
          );
        }
      }

      throw error;
    }
  },

  async forceManualClassroomSync(
    organizationId: number,
    peerGroupId: number,
    isCreator: boolean,
    retriesAllowed = 2
  ): Promise<ApiPeerGroup> {
    try {
      const {
        data: { data: result }
      } = await axios.put(
        pathBuilder.basic(
          'organizations',
          `${organizationId}`,
          'google-classroom-courses',
          `${peerGroupId}`
        ),
        {}
      );

      return parseToApiPeerGroup(camelcaseKeys(result, { deep: true }));
    } catch (error) {
      if (retriesAllowed == 0) {
        throw error;
      }

      /// We allow any teacher or admin to force a sync, but we only
      /// store/use the creator's refresh token to sync/import.
      ///
      /// If this becomes an issue down the line:
      ///
      /// - If infrequent, we can manually change the `created_by_id` on the peer_group
      /// - If frequent we could add a `synced_by_id` to the peer_group
      if (!isCreator) {
        throw error;
      }

      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response?.status === 403) {
        const body = camelcaseKeys(axiosError.response.data);

        if (body.authorizationRequired === 'google') {
          const authorizationCode = await grantOfflineAccess();

          await this.allowAccessToGoogleApi(authorizationCode);

          // Retry the import
          return this.forceManualClassroomSync(
            organizationId,
            peerGroupId,
            isCreator,
            retriesAllowed--
          );
        }
      }

      throw error;
    }
  }
};
